import { useEffect, useState } from "react"
import { INewReplenishmentResponse } from "interfaces/IReplenishment"
import { useLocation, useNavigate } from "react-router"
import { Header, InfoText, StrongText } from "./styles"
import { Box, MenuItem, Select, Typography, CircularProgress } from "@mui/material"
import { toast } from "react-toastify"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { configsService } from "services/configsService"
import { IConfigDataResponse } from "interfaces/IConfig"
import Timer from "shared/Timer"
import { replenishmentService } from "services/replenishmentService"
import { productService } from "services/productService"
import { batchService } from "services/batchService"
import Cam from "utils/Cam"

const MoveSkuReplenishing = () => {
  const [successFeedback, setSuccessFeedback] = useState(false)
  const [isLoading, setisLoading] = useState(false)
  const [isLoadingBatches, setIsLoadingBatches] = useState(false)
  const [marketTaken, setMarketTaken] = useState(0)
  const [marketOptions, setMarketOptions] = useState<number[]>([])
  const [newReplenishment, setNewReplenishment] = useState<INewReplenishmentResponse | undefined>(
    undefined,
  )
  const [config, setConfig] = useState<IConfigDataResponse>({
    id: 0,
    created: "",
    totalMarkets: 0,
    modifiedById: 0,
    modifiedByUsername: "",
    useScanner: false,
  })
  const [inputValue, setInputValue] = useState("")
  const [batches, setBatches] = useState<number[]>([])
  const [selectedBatch, setSelectedBatch] = useState<number | null>(null)
  const [showScanner, setShowScanner] = useState(true)
  const [isConfigLoading, setIsConfigLoading] = useState(true)

  const location = useLocation()
  const disableConfirmBotton =
    !inputValue ||
    !marketTaken ||
    isLoading ||
    Number(inputValue) < 1 ||
    isNaN(Number(inputValue)) ||
    !selectedBatch
  const navigate = useNavigate()

  const handleInputChange = (event: any) => {
    setInputValue(event.target.value)
  }

  const handleTakenMarketNumberChange = (event: any) => {
    setMarketTaken(event.target.value)
  }

  const handleBatchChange = (event: any) => {
    setSelectedBatch(event.target.value)
  }

  const handleMarketOption = (marketNumberBlocked: number) => {
    const generateNumberList =
      config.totalMarkets > 0
        ? Array.from({ length: config.totalMarkets }, (_, i) => i + 1).filter(
            num => num !== marketNumberBlocked,
          )
        : []
    setMarketOptions(generateNumberList)
  }

  const handleGetConfigs = async () => {
    try {
      setIsConfigLoading(true)
      const response_config = await configsService.getConfig()
      setConfig(response_config)
    } catch (error: any) {
      toast(
        error?.message ?? "Ocorreu um erro ao buscar as configurações, dá um alô pro time de tech.",
        { type: "error" },
      )
    } finally {
      setIsConfigLoading(false)
    }
  }

  const handleGetBatches = async () => {
    if (!newReplenishment?.skuData?.productBaseId) return
    try {
      setIsLoadingBatches(true)
      const response = await batchService.getAllBaseProducts({
        productBaseId: newReplenishment.skuData.productBaseId,
      })
      const batchIds = response.results[0]?.Batchs?.map(batch => batch.batchId) || []
      setBatches(batchIds)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro ao buscar os lotes, dá um alô pro time de tech.", {
        type: "error",
      })
    } finally {
      setIsLoadingBatches(false)
    }
  }

  const handleConfirmReplenish = async () => {
    try {
      setisLoading(true)
      const response_config = await replenishmentService.confirmSkuMove(
        newReplenishment?.replenishmentData?.productId,
        {
          quantityMoved: Number(inputValue),
          marketNumberTakenFrom: marketTaken,
          marketNumberReceiving: newReplenishment?.replenishmentData?.marketNumber,
          workId: newReplenishment?.replenishmentData?.workId,
          replenishmentId: newReplenishment?.replenishmentData?.id,
          batchId: selectedBatch,
        },
      )
      setConfig(response_config)
      setMarketTaken(Number(0))
      setSuccessFeedback(true)
    } catch (error: any) {
      toast(
        error?.message ?? "Ocorreu um erro ao buscar as configurações, dá um alô pro time de tech.",
        { type: "error" },
      )
    } finally {
      setisLoading(false)
    }
  }

  const handleNewReplenish = () => {
    setisLoading(true)
    setSuccessFeedback(false)
    navigate("/painel-de-operador/abastecimento")
    setisLoading(false)
  }

  const handleScanResult = async (code: string) => {
    try {
      setisLoading(true)
      const response = await productService.checkBatch(code, newReplenishment?.skuData?.sku || "")
      if (response.skuData) {
        setNewReplenishment(prev => ({
          ...prev!,
          skuData: {
            productId: response.skuData.productId,
            sku: response.skuData.sku,
            purchaseValue: response.skuData.purchaseValue.toString(),
            portionType: response.skuData.portionType,
            separationOrder: response.skuData.separationOrder,
            name: response.skuData.name,
            productBaseId: response.skuData.productBaseId,
          },
        }))
        setSelectedBatch(response.batch_id)
        setShowScanner(false)
      } else {
        toast("SKU não encontrado", { type: "error" })
      }
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro ao buscar o SKU, dá um alô pro time de tech.", {
        type: "error",
      })
    } finally {
      setisLoading(false)
    }
  }

  const handleScanAgain = () => {
    setShowScanner(true)
    setSelectedBatch(null)
  }

  useEffect(() => {
    if (location.state && location.state.newReplenishment) {
      setNewReplenishment(location.state.newReplenishment)
      setMarketTaken(location.state.newReplenishment.replenishmentData?.market_sku_moved)
      handleGetConfigs()
      if (!config.useScanner) {
        handleGetBatches()
      } else {
        setShowScanner(true)
      }
    }
  }, [location.state])

  useEffect(() => {
    if (newReplenishment?.skuData?.productId && !config.useScanner) {
      handleGetBatches()
    }
  }, [newReplenishment?.skuData?.productId, config.useScanner])

  useEffect(() => {
    if (config.totalMarkets > 0) {
      handleMarketOption(location.state?.newReplenishment?.replenishmentData?.marketNumber)
    }
  }, [config])

  useEffect(() => {
    if (config.useScanner) {
      setShowScanner(true)
    }
  }, [config.useScanner])

  return (
    <Box>
      <Header component="header">
        <Box>
          <InfoText>
            Produto:{" "}
            <StrongText>{newReplenishment?.skuData?.name || "Ainda não definido"}</StrongText>
          </InfoText>
          <InfoText>
            Porcionamento:{" "}
            <StrongText>
              {newReplenishment?.skuData?.purchaseValue}{" "}
              {newReplenishment?.skuData?.portionType === "peso"
                ? "kg"
                : newReplenishment?.skuData?.portionType === "unidade"
                  ? "un"
                  : "Não definido"}
            </StrongText>
          </InfoText>
          <InfoText>
            Etapa:{" "}
            <StrongText>
              {newReplenishment?.skuData?.separationOrder || "Ainda não definido"}
            </StrongText>
          </InfoText>
          <InfoText>
            Mercado:{" "}
            <StrongText>
              {newReplenishment?.replenishmentData?.marketNumber || "Ainda não definido"}
            </StrongText>
          </InfoText>
          <InfoText>
            Tempo de abastecimento:
            <StrongText>
              <Timer time={newReplenishment?.workData?.begin} stop={successFeedback} />
            </StrongText>
          </InfoText>
          {config.useScanner && (
            <InfoText>
              Lote: <StrongText>{selectedBatch || "Ainda não definido"}</StrongText>
            </InfoText>
          )}
        </Box>
      </Header>
      {successFeedback ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          marginTop="50px"
        >
          <CheckCircleIcon sx={{ fontSize: 100, color: "#FF8C42" }} />
          <Typography
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: "500",
              fontSize: "21px",
              lineHeight: "33.6px",
              letterSpacing: "1%",
              marginTop: "20px",
            }}
          >
            Produto abastecido! 👍
          </Typography>
          <button
            onClick={handleNewReplenish}
            disabled={isLoading}
            style={{
              marginTop: "40px",
              padding: "10px 20px",
              fontSize: "18px",
              backgroundColor: "#FF8C42",
              color: "white",
              border: "none",
              borderRadius: "16px",
              cursor: "pointer",
              width: "300px",
            }}
          >
            beleza, valeu!
          </button>
        </Box>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "2rem",
          }}
        >
          <Typography
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: "500",
              fontSize: "25px",
              lineHeight: "33.6px",
              letterSpacing: "1%",
              marginBottom: "2rem",
            }}
          >
            Remanejo entre mercados
          </Typography>
          {config.useScanner && showScanner ? (
            <div style={{ marginBottom: "20px", textAlign: "center" }}>
              <Typography
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: "500",
                  fontSize: "20px",
                  lineHeight: "33.6px",
                  letterSpacing: "1%",
                  marginBottom: "1rem",
                }}
              >
                Escaneie a etiqueta do produto
              </Typography>
              <Cam onSubmit={handleScanResult} loading={isLoading} />
            </div>
          ) : (
            <>
              {config.useScanner && selectedBatch && (
                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                  <button
                    onClick={handleScanAgain}
                    style={{
                      padding: "10px 20px",
                      fontSize: "18px",
                      backgroundColor: "#FF8C42",
                      color: "white",
                      border: "none",
                      borderRadius: "16px",
                      cursor: "pointer",
                      width: "300px",
                    }}
                  >
                    Escanear novamente
                  </button>
                </div>
              )}
              {!config.useScanner && (
                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                  <Typography
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: "500",
                      fontSize: "20px",
                      lineHeight: "33.6px",
                      letterSpacing: "1%",
                      marginBottom: "1rem",
                    }}
                  >
                    Selecione o lote
                  </Typography>
                  {isLoadingBatches ? (
                    <CircularProgress />
                  ) : (
                    <Select
                      value={selectedBatch ?? ""}
                      onChange={handleBatchChange}
                      displayEmpty
                      style={{
                        padding: "8px",
                        fontSize: "18px",
                        width: "260px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        textAlign: "center",
                        height: "2.5rem",
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            overflowY: "auto",
                          },
                        },
                      }}
                      renderValue={selected => {
                        if (!selected) {
                          return "Selecione..."
                        }
                        return selected
                      }}
                    >
                      {batches.length > 0 ? (
                        batches.map(batch => (
                          <MenuItem key={batch} value={batch}>
                            {batch}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          Nenhum lote encontrado, informe o PCP
                        </MenuItem>
                      )}
                    </Select>
                  )}
                </div>
              )}
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <label
                  htmlFor="marketNumber"
                  style={{
                    display: "block",
                    fontSize: "18px",
                    fontWeight: "500",
                    marginBottom: "0.5rem",
                  }}
                >
                  <Typography
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: "500",
                      fontSize: "20px",
                      lineHeight: "33.6px",
                      letterSpacing: "1%",
                    }}
                  >
                    Sugerimos retirar do:
                  </Typography>
                </label>
                <Select
                  id="TakenMarketNumber"
                  value={marketTaken}
                  onChange={handleTakenMarketNumberChange}
                  displayEmpty
                  style={{
                    padding: "8px",
                    fontSize: "18px",
                    width: "260px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    textAlign: "center",
                    height: "2.5rem",
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                      },
                    },
                  }}
                  renderValue={selected => {
                    if (!selected) {
                      return "Selecione..."
                    }
                    return `Mercado ${selected}`
                  }}
                >
                  {marketOptions.map(number => (
                    <MenuItem key={number} value={number}>
                      Mercado {number}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <Typography
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: "500",
                  fontSize: "20px",
                  lineHeight: "33.6px",
                  letterSpacing: "1%",
                }}
              >
                Quantos pacotes retirou?
              </Typography>

              <input
                type="number"
                min="1"
                value={inputValue}
                onChange={handleInputChange}
                style={{
                  padding: "8px",
                  fontSize: "18px",
                  width: "260px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  textAlign: "center",
                  marginTop: "0.5rem",
                  marginBottom: "0.5rem",
                }}
                placeholder="Ex. 40"
              />

              <button
                disabled={disableConfirmBotton}
                onClick={handleConfirmReplenish}
                style={{
                  marginTop: "1rem",
                  padding: "10px 20px",
                  fontSize: "18px",
                  backgroundColor: disableConfirmBotton ? "#FFC78F" : "#FF8C42",
                  cursor: disableConfirmBotton ? "not-allowed" : "pointer",
                  color: "white",
                  border: "none",
                  borderRadius: "16px",
                  width: "300px",
                }}
              >
                Realocar
              </button>
            </>
          )}
        </div>
      )}
    </Box>
  )
}

export default MoveSkuReplenishing
